import React, { useEffect, useState } from 'react';
import { loadPlaidScript, createUpdateLinkToken } from '../../services/plaidService';
import './PlaidLinkModal.css';
import InlineAlert from '../common/InlineAlert';
import { getUserFacingError } from '../../lib/userFacingErrors';

/**
 * PlaidLinkModal - Handles opening Plaid Link in update mode
 * @param {Object} props
 * @param {string} props.itemId - The Plaid item ID to update
 * @param {Function} props.onSuccess - Callback when update is successful
 * @param {Function} props.onExit - Callback when user exits Plaid Link
 * @param {Function} props.onClose - Callback to close the modal
 */
export default function PlaidLinkModal({ itemId, onSuccess, onExit, onClose }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [linkHandler, setLinkHandler] = useState(null);
  const [plaidOpened, setPlaidOpened] = useState(false);

  useEffect(() => {
    let isMounted = true;
    let handler = null;

    async function initializePlaidLink() {
      try {
        setLoading(true);
        setError(null);

        // Load Plaid script
        await loadPlaidScript();

        if (!isMounted) return;

        // Create update mode link token
        const { link_token } = await createUpdateLinkToken(itemId);

        if (!isMounted) return;

        // Create Plaid Link handler
        handler = window.Plaid.create({
          token: link_token,
          onSuccess: (public_token, metadata) => {
            // In update mode, we don't need to exchange the token
            // The access token remains the same
            if (onSuccess) onSuccess(metadata);
            if (onClose) onClose();
          },
          onExit: (err, metadata) => {
            if (onExit) onExit(err, metadata);
            if (onClose) onClose();
          },
        });

        if (!isMounted) return;

        setLinkHandler(handler);
        setLoading(false);

        // Auto-open Link and hide our modal
        handler.open();
        setPlaidOpened(true);
      } catch (err) {
        console.error('Error initializing Plaid Link:', err);
        if (isMounted) {
          setError(getUserFacingError(err, 'plaid-link'));
          setLoading(false);
        }
      }
    }

    initializePlaidLink();

    return () => {
      isMounted = false;
      if (handler) {
        handler.exit();
      }
    };
  }, [itemId, onSuccess, onExit, onClose]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      if (linkHandler) {
        linkHandler.exit();
      }
      if (onClose) onClose();
    }
  };

  // Hide modal once Plaid Link has opened
  if (plaidOpened) {
    return null;
  }

  return (
    <div className="plaid-link-modal-backdrop" onClick={handleBackdropClick}>
      <div className="plaid-link-modal-content">
        {loading && (
          <div className="plaid-link-modal-loading">
            <div className="plaid-link-modal-spinner"></div>
            <p>Opening Plaid Link...</p>
            <p className="plaid-link-modal-subtitle">Please wait while we prepare your connection</p>
          </div>
        )}
        {error && (
          <div className="plaid-link-modal-error">
            <InlineAlert
              compact
              title={error.title}
              message={error.message}
            />
            <button onClick={onClose} className="plaid-link-modal-close-btn">
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
